import React, { useState, useEffect, useRef } from "react";
import { ShieldCheck, GraduationCap, School, MoreVertical, Trash2, Sparkles } from "lucide-react";

const ChildCard = ({ student, onAuthenticate, onRemoveChild }) => {
  const { name, imageUrl, grade, school, isAuthSetup } = student;
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Check if student is enrolled in any active or pending courses
  const isEnrolled = Boolean(
    student.enrolment?.["ai-lab"] ||
    (student.enrolments && Object.keys(student.enrolments).length > 0) ||
    (Array.isArray(student.courses) && student.courses.length > 0) ||
    student.isEnrolled
  );

  // Generate initials for avatar placeholder
  const getInitials = (fullName) => {
    if (!fullName) return "?";
    return fullName
      .split(" ")
      .slice(0, 2)
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const formattedGrade = grade
    ? grade.toLowerCase().startsWith("class")
      ? grade
      : `Class ${grade}`
    : "Class N/A";

  return (
    <div className="bg-white border border-slate-200/80 hover:border-blue-300 rounded-2xl p-4 w-full sm:w-80 shadow-2xs hover:shadow-md transition-all duration-300 relative text-left">
      <div className="flex items-start justify-between gap-3">
        {/* Left Side: Avatar + Details */}
        <div className="flex items-start gap-3.5 min-w-0 flex-1">
          {/* Avatar */}
          <div className="flex-shrink-0 mt-0.5">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={name}
                className="w-11 h-11 rounded-xl object-cover border border-slate-100 shadow-2xs ring-2 ring-slate-50"
              />
            ) : (
              <div className="w-11 h-11 rounded-xl bg-gradient-to-tr from-blue-600 via-indigo-600 to-purple-600 text-white flex items-center justify-center text-xs font-black shadow-2xs ring-2 ring-white">
                {getInitials(name)}
              </div>
            )}
          </div>

          {/* Child Name & Details */}
          <div className="space-y-1 min-w-0 flex-1">
            <h4 className="text-sm sm:text-base font-extrabold text-slate-900 capitalize truncate leading-tight">
              {name}
            </h4>

            {/* Sub-info line: Grade & Enrolled Status */}
            <div className="flex items-center gap-1.5 text-xs text-slate-500 flex-wrap">
              <span className="inline-flex items-center gap-1 font-bold text-blue-700 bg-blue-50/80 px-2 py-0.5 rounded-md text-[10px] border border-blue-100/60">
                <GraduationCap className="w-3 h-3 text-blue-500 flex-shrink-0" />
                {formattedGrade}
              </span>

              {isEnrolled && (
                <span className="inline-flex items-center gap-1 font-extrabold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md text-[10px] border border-emerald-100">
                  <Sparkles className="w-2.5 h-2.5 text-amber-500" />
                  Enrolled
                </span>
              )}
            </div>

            {/* School Name */}
            <div className="flex items-center gap-1 text-[11px] text-slate-400 font-medium pt-0.5 truncate">
              <School className="w-3 h-3 text-slate-350 flex-shrink-0" />
              <span className="truncate">{school || "No School Added"}</span>
            </div>
          </div>
        </div>

        {/* Three-Dot Menu Options */}
        <div className="relative flex-shrink-0 self-start" ref={menuRef}>
          <button
            type="button"
            onClick={() => setShowMenu((prev) => !prev)}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition cursor-pointer"
            title="Options"
          >
            <MoreVertical className="w-4 h-4" />
          </button>

          {showMenu && (
            <div className="absolute right-0 top-8 z-30 w-44 bg-white rounded-2xl shadow-xl border border-slate-150 p-1.5 text-left animate-fade-in">
              <button
                type="button"
                disabled={isEnrolled}
                onClick={() => {
                  setShowMenu(false);
                  if (!isEnrolled && onRemoveChild) {
                    onRemoveChild(student);
                  }
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-bold rounded-xl transition ${
                  isEnrolled
                    ? "text-slate-300 cursor-not-allowed bg-slate-50"
                    : "text-red-600 hover:bg-red-50 cursor-pointer"
                }`}
                title={isEnrolled ? "Cannot remove student enrolled in courses" : "Remove child"}
              >
                <Trash2 className="w-3.5 h-3.5 flex-shrink-0" />
                <div className="flex flex-col">
                  <span>Remove Child</span>
                  {isEnrolled && (
                    <span className="text-[9px] text-slate-400 font-normal">Enrolled in courses</span>
                  )}
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Commented out authentication button code for future use */}
      {/*
      {!isAuthSetup && (
        <div className="mt-3 pt-2 border-t border-slate-100">
          <button
            type="button"
            className="w-full py-1.5 px-3 bg-gradient-to-r from-red-500 to-red-700 text-white rounded-xl text-xs font-black shadow-3xs"
            onClick={() => onAuthenticate && onAuthenticate(student)}
          >
            <ShieldCheck className="w-4 h-4 text-white" />
            Authenticate Now
          </button>
        </div>
      )}
      */}
    </div>
  );
};

export default ChildCard;
